import { getMessaging, getToken, onMessage, isSupported, Messaging } from "firebase/messaging";
import { httpsCallable } from "firebase/functions";
import { doc, updateDoc, arrayUnion } from "firebase/firestore";
import { app, auth, db, functions } from "../firebase";

const VAPID_KEY = "BCarcCkdlfH31A2JdAeL8uzzKHBLfy7jIz0HNJeEHKN5eTHGVJhOmp-QleVgyiQGxavnFUGtKSffhnuDViAcU30";

let _messaging: Messaging | null = null;

async function getMessagingInstance(): Promise<Messaging | null> {
  if (_messaging) return _messaging;
  try {
    const supported = await isSupported();
    if (!supported) return null;
    _messaging = getMessaging(app);
    return _messaging;
  } catch (e) {
    console.warn('[FCM] Messaging not supported:', e);
    return null;
  }
}

async function getTokenWithRetry(m: Messaging, swReg: ServiceWorkerRegistration, maxRetries = 3, initialDelay = 1000): Promise<string | null> {
  let attempts = 0;
  while (attempts < maxRetries) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10s timeout

      const token = await getToken(m, {
        vapidKey: VAPID_KEY,
        serviceWorkerRegistration: swReg
      });

      clearTimeout(timeoutId);
      return token;
    } catch (err: any) {
      attempts++;
      if (attempts >= maxRetries || err.code === 'messaging/permission-blocked') {
        throw err;
      }
      const delay = initialDelay * Math.pow(2, attempts - 1);
      console.warn(`[FCM] Token attempt ${attempts} failed, retrying in ${delay}ms...`, err);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  return null;
}

export async function getFCMToken(): Promise<string | null> {
  try {
    const m = await getMessagingInstance();
    if (!m) return null;

    // Prevent hanging indefinitely if service worker fails to activate
    const swReg = await Promise.race([
      navigator.serviceWorker.ready,
      new Promise<never>((_, reject) => setTimeout(() => reject(new Error('Service Worker ready timeout')), 6000))
    ]);

    // iOS Safari race condition fix
    const isIOS = /iphone|ipad|ipod|macintosh/i.test(navigator.userAgent) && 'ontouchend' in document;
    if (isIOS) {
      await new Promise((resolve) => setTimeout(resolve, 800));
    }

    // Prevent hanging indefinitely on token fetch
    const token = await Promise.race([
      getTokenWithRetry(m, swReg),
      new Promise<null>((resolve) => setTimeout(() => {
        console.warn('[FCM] getTokenWithRetry timed out');
        resolve(null);
      }, 10000))
    ]);

    if (token) {
      try {
        const subscribeToTopicFn = httpsCallable(functions, 'subscribeToTopic');
        await subscribeToTopicFn({ token, topic: 'app_updates' });
      } catch (subErr) {
        console.warn('[FCM] Topic subscription failed:', subErr);
      }
    }

    return token;
  } catch (e) {
    console.warn('[FCM] getFCMToken failed after retries or timed out:', e);
    return null;
  }
}

export async function onForegroundMessage(callback: (payload: any) => void): Promise<() => void> {
  const m = await getMessagingInstance();
  if (!m) return () => {};
  return onMessage(m, callback);
}

export async function savePushToken(token: string): Promise<void> {
  const user = auth.currentUser;
  if (!user || !token) return;
  try {
    await updateDoc(doc(db, 'members', user.uid), {
      pushTokens: arrayUnion(token),
      fcmTokens: arrayUnion(token),
      pushTokenUpdatedAt: new Date().toISOString()
    });
  } catch (e) {
    console.warn('[savePushToken] Failed to save token:', e);
  }
}
